import { useCallback, useMemo } from "react";
import { INonFlyingType } from "@vmosolutions/oc-interfaces";
import { FormikSelectField } from "src/components/FormikComponents/FormikWrappers";
import { ISelectFieldProps } from "src/components/FormikComponents/SelectField";
// API Calls
import { formatFunction, parseFunction } from "./helpers";

import { useSelector } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";

interface INonFlyingTypeSelectProps extends Omit<ISelectFieldProps, "options"> {
  mosaicPane: HTMLElement;
  valueKey?: string; // valueKey sent back as value - use entire object otherwise.
  showInactive?: boolean;
}

/** Sort types list Alphabetically */
const sortTypesByDescription = (a: INonFlyingType, b: INonFlyingType): number => {
  return b.description > a.description ? -1 : 1;
};

/**
 * Sepcialized wrapper for "built-in" oc non flying type select field.
 * @param props -
 */
export const NonFlyingTypeSelectField = (props: INonFlyingTypeSelectProps): JSX.Element => {
  const { mosaicPane, label, name, onChange, valueKey, isMulti, defaultValue, ...selectProps } = props;

  const structures = useSelector((state: IRootState) => state.structures);
  const nonFlyingTypes = useMemo(() => {
    return structures?.customerStructure?.nonFlyingTypes ?? [];
  }, [structures?.customerStructure?.nonFlyingTypes]);

  const options = useMemo(() => nonFlyingTypes.sort(sortTypesByDescription), [nonFlyingTypes]);

  // Setup Format And Parse functions (used if valueKey is passed)
  const formatValue = useCallback(
    (value: string) => formatFunction(value, nonFlyingTypes, valueKey, isMulti),
    [nonFlyingTypes, valueKey, isMulti]
  );
  const parseOption = useCallback((option: string) => parseFunction(option, valueKey, isMulti), [valueKey, isMulti]);

  return (
    <FormikSelectField
      name={name}
      label={label ?? "Non-Flying Type Selector"}
      isMulti={isMulti}
      options={options}
      getOptionLabel={(type: INonFlyingType) => `${type.description} [${type.title}]`}
      getOptionValue={(type: INonFlyingType) => type._id}
      format={(value) => (valueKey ? formatValue(value) : value)}
      parse={(option) => (valueKey ? parseOption(option) : option)}
      onChange={(value: any) => !!onChange && onChange(value)}
      menuPortalTarget={mosaicPane}
      menuPosition={mosaicPane ? "fixed" : undefined}
      isSearchable={true}
      defaultValue={defaultValue}
      openMenuOnFocus={false}
      isClearable={true}
      placeholder={`Select Non-Flying Type${isMulti && "s"}`}
      {...selectProps} // override with any props passed in such as isMulti, format, parse.
    />
  );
};
